import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowUpRight, Sparkles, MapPin } from 'lucide-react';
import { Reveal, Magnetic, WordReveal } from '../components/Motion';
import { expertise, metrics, featuredWork, philosophy, testimonials, trustedBy, labs } from '../mock/data';
import { journalArticles } from '../mock/journal';

const Home = () => (
  <div className="relative">
    <Hero/>
    <TrustedMarquee/>
    <FeaturedWork/>
    <Expertise/>
    <PhilosophyBand/>
    <Metrics/>
    <AILabTeaser/>
    <Testimonials/>
    <JournalTeaser/>
  </div>
);

const Hero = () => {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], [0, -200]);
  return (
    <section className="relative min-h-[88vh] flex flex-col justify-center overflow-hidden px-6 md:px-12 lg:px-20 pb-16 pt-32">
      {/* Portrait \u2014 low-visibility background on the top-right */}
      <div className="pointer-events-none absolute top-0 right-0 h-full w-[55%] md:w-[45%] lg:w-[40%] z-0" aria-hidden>
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_portfolio-showcase-2374/artifacts/17hx1lsk_liftapp%20%283%29.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center right',
            backgroundRepeat: 'no-repeat',
            opacity: 0.18,
            filter: 'grayscale(35%) contrast(1.05)',
            maskImage: 'linear-gradient(to left, rgba(0,0,0,1) 30%, rgba(0,0,0,0) 95%)',
            WebkitMaskImage: 'linear-gradient(to left, rgba(0,0,0,1) 30%, rgba(0,0,0,0) 95%)',
          }}
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(11,11,16,0.35) 0%, rgba(11,11,16,0.75) 100%)' }}/>
      </div>

      <motion.div className="blob" style={{ width: 600, height: 600, background: 'var(--accent)', top: -200, right: -150, opacity: 0.16, y }}/>
      <motion.div className="blob" style={{ width: 500, height: 500, background: '#7E6BFF', bottom: -200, left: -200, opacity: 0.12 }}/>
      <div className="max-w-[1500px] mx-auto w-full relative z-10">
        <Reveal>
          <div className="flex items-center gap-3 text-[12px] tracking-widest uppercase text-muted">
            <span className="inline-block w-2 h-2 rounded-full bg-accent animate-pulse"></span>
            Amrit Raj Singh &mdash; Product Design Manager
          </div>
        </Reveal>

        <h1 className="h-display mt-8 text-[12vw] md:text-[8.5vw] lg:text-[5.5vw] leading-[0.95] max-w-[14ch]">
          <WordReveal text="Designing products people"/> <span className="font-serif-i text-accent">love to use.</span>
        </h1>

        <div className="mt-12 grid md:grid-cols-12 gap-8 items-end">
          <Reveal className="md:col-span-7" delay={0.4}>
            <p className="text-[16px] md:text-[17px] leading-[1.75] text-muted max-w-2xl">
              I&apos;m a Product Design Manager with <span className="text-fg">11+ years</span> across UI/UX, design systems and AI-enabled experiences. Currently leading a team of up to 10 designers at <span className="text-fg">Uppwise</span>. Certified by IIT Roorkee, Google UX and CSM.
            </p>
          </Reveal>
          <Reveal className="md:col-span-5 flex md:justify-end items-end gap-3 flex-wrap" delay={0.5}>
            <Magnetic><Link to="/work" className="btn-pill solid" data-testid="home-view-work-cta">View selected work <ArrowUpRight size={14}/></Link></Magnetic>
            <Magnetic><Link to="/about" className="btn-pill"><Sparkles size={14}/> About me</Link></Magnetic>
          </Reveal>
        </div>

        <div className="mt-16 grid md:grid-cols-3 gap-6 text-[12px] uppercase tracking-widest text-dim">
          <div className="flex items-center gap-2"><MapPin size={13}/> Noida, India &mdash; working globally</div>
          <div className="hidden md:block">v.7 &mdash; 2026 edition</div>
          <div className="hidden md:block text-right">Scroll to explore &darr;</div>
        </div>
      </div>
    </section>
  );
};

const TrustedMarquee = () => (
  <section className="py-12 border-y" style={{ borderColor: 'var(--line)' }}>
    <div className="overflow-hidden">
      <div className="marquee-track text-dim">
        {[...trustedBy, ...trustedBy.map(b => ({ ...b, id: `${b.id}-dup` }))].map((b) => (
          <span key={b.id} className="flex items-center gap-16 px-8">
            <img
              src={b.logo}
              alt="Client Logo"
              className="marquee-logo"
            />
          </span>
        ))}
      </div>
    </div>
  </section>
);

const FEATURED_SLUGS = { fw1: 'spm', fw2: 'vitasync', fw3: 'doc-connect', fw4: 'iconography' };

const FeaturedWork = () => (
  <section className="px-6 md:px-12 lg:px-20 py-32">
    <div className="max-w-[1500px] mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-6">
        <div>
          <span className="section-eyebrow">Selected works</span>
          <h2 className="h-display mt-6 text-6xl md:text-8xl">A curated <span className="font-serif-i text-accent">few.</span></h2>
        </div>
        <Link to="/ux-folio" className="link-arrow">All case studies <span className="arrow"><ArrowUpRight size={14}/></span></Link>
      </div>

      <div className="mt-20 grid md:grid-cols-12 gap-y-24 gap-x-8">
        {featuredWork.map((w, i) => (
          <Reveal key={w.id} delay={i * 0.05} className={`md:col-span-6 ${i % 2 === 1 ? 'md:mt-32' : ''}`}>
            <Link to={`/work/${FEATURED_SLUGS[w.id] || 'spm'}`} className="group block" data-cursor="hover">
              <div className="relative aspect-[5/4] overflow-hidden rounded-2xl ring-glow">
                <img src={w.image} alt={w.title} className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-110"/>
                <div className="absolute inset-0" style={{ background: 'linear-gradient(transparent 55%, rgb(0 0 0 / 99%))' }}></div>
                <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                  <span className="text-[12px] tracking-widest uppercase" style={{ color: w.color }}>{w.industry}</span>
                  <span className="text-[12px] text-muted">{w.year}</span>
                </div>
              </div>
              <div className="mt-5 flex items-start justify-between gap-6">
                <div>
                  <h3 className="h-display text-2xl md:text-3xl">{w.title}</h3>
                  <p className="mt-2 text-muted text-[13px]">{w.role}</p>
                </div>
                <span className="arrow w-10 h-10 rounded-full inline-flex items-center justify-center border" style={{ borderColor: 'var(--line-strong)' }}><ArrowUpRight size={16}/></span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Expertise = () => (
  <section className="px-6 md:px-12 lg:px-20 py-32" style={{ background: 'var(--bg-2)' }}>
    <div className="max-w-[1500px] mx-auto">
      <div className="grid md:grid-cols-12 gap-12 items-start">
        <Reveal className="md:col-span-5 md:sticky md:top-28">
          <span className="section-eyebrow">Capabilities</span>
          <h2 className="h-display mt-6 text-5xl md:text-7xl">Six things, <span className="font-serif-i text-accent">done well.</span></h2>
          <p className="mt-8 text-muted max-w-md">A focused practice. Deep, not broad. End-to-end ownership from problem definition to Figma handoff.</p>
        </Reveal>
        <div className="md:col-span-7 grid sm:grid-cols-2 gap-5">
          {expertise.map((e, i) => (
            <Reveal key={e.id} delay={i * 0.05}>
              <div className="card-prem p-7 h-full">
                <div className="flex items-center justify-between">
                  <span className="text-dim font-display">{e.tag}</span>
                  <ArrowUpRight size={16} className="text-dim"/>
                </div>
                <h3 className="font-display text-2xl mt-10">{e.label}</h3>
                <p className="mt-3 text-[13px] text-muted leading-relaxed">{e.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const PhilosophyBand = () => (
  <section className="px-6 md:px-12 lg:px-20 py-32">
    <div className="max-w-[1500px] mx-auto">
      <span className="section-eyebrow">Operating principles</span>
      <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-12">
        {philosophy.map((p, i) => (
          <Reveal key={p.id} delay={i * 0.07}>
            <div>
              <div className="text-dim font-display text-sm">0{i+1}</div>
              <div className="h-display text-4xl md:text-6xl mt-4">{p.word}</div>
              <div className="text-muted text-[13px] mt-3 font-serif-i">{p.sub}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Metrics = () => (
  <section className="px-6 md:px-12 lg:px-20 py-20 border-y" style={{ borderColor: 'var(--line)' }}>
    <div className="max-w-[1500px] mx-auto grid grid-cols-2 md:grid-cols-4 gap-10">
      {metrics.map((m, i) => (
        <Reveal key={m.id} delay={i * 0.05}>
          <div className="h-display text-5xl md:text-7xl text-accent">{m.value}</div>
          <div className="text-dim text-[12px] uppercase tracking-widest mt-3">{m.label}</div>
        </Reveal>
      ))}
    </div>
  </section>
);

const AILabTeaser = () => (
  <section className="px-6 md:px-12 lg:px-20 py-32 relative overflow-hidden">
    <div className="blob" style={{ width: 500, height: 500, background: 'var(--accent)', top: '20%', right: -150, opacity: 0.15 }}/>
    <div className="max-w-[1500px] mx-auto relative">
      <div className="grid md:grid-cols-12 gap-10 items-end">
        <div className="md:col-span-7">
          <span className="section-eyebrow">AI Experience Lab</span>
          <h2 className="h-display mt-6 text-6xl md:text-8xl">Designing for <span className="font-serif-i text-accent">unfamiliar</span> futures.</h2>
        </div>
        <div className="md:col-span-5">
          <p className="text-muted leading-relaxed">A live notebook of small experiments — FigGPT loops, Galileo, UX Pilot, v0 and Gemini. Some ship, some teach, all are public.</p>
          <Link to="/ai" className="link-arrow mt-6">Enter the lab <span className="arrow"><ArrowUpRight size={14}/></span></Link>
        </div>
      </div>
      <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        {labs.slice(0, 4).map((l, i) => (
          <Reveal key={l.id} delay={i * 0.06}>
            <div className="card-prem overflow-hidden h-full group" data-cursor="hover">
              <div className="aspect-[4/3] overflow-hidden">
                <img src={l.image} alt={l.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1200ms]"/>
              </div>
              <div className="p-6">
                <span className="text-[11px] uppercase tracking-widest text-accent">{l.tag}</span>
                <h4 className="font-display text-lg mt-3">{l.title}</h4>
                <p className="text-[12.5px] text-muted mt-2 leading-relaxed">{l.body}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Testimonials = () => (
  <section className="px-6 md:px-12 lg:px-20 py-32" style={{ background: 'var(--bg-2)' }}>
    <div className="max-w-[1500px] mx-auto">
      <span className="section-eyebrow">Kind words</span>
      <div className="mt-12 grid md:grid-cols-3 gap-6">
        {testimonials.map((t, i) => (
          <Reveal key={t.id} delay={i * 0.06}>
            <div className="card-prem p-8 h-full flex flex-col">
              <div className="text-accent font-serif-i text-6xl leading-none">“</div>
              <p className="mt-2 text-[15px] leading-relaxed">{t.quote}</p>
              <div className="mt-auto pt-8">
                <p className="text-sm font-medium">{t.author}</p>
                <p className="text-muted text-[12px]">{t.role}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const JournalTeaser = () => (
  <section className="px-6 md:px-12 lg:px-20 py-32">
    <div className="max-w-[1500px] mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-6">
        <div>
          <span className="section-eyebrow">Journal</span>
          <h2 className="h-display mt-6 text-6xl md:text-8xl">Field <span className="font-serif-i text-accent">notes.</span></h2>
        </div>
        <Link to="/blog" className="link-arrow">All entries <span className="arrow"><ArrowUpRight size={14}/></span></Link>
      </div>
      <div className="mt-16 grid md:grid-cols-3 gap-6">
        {journalArticles.slice(0, 3).map((j, i) => (
          <Reveal key={j.id} delay={i * 0.05}>
            <Link to={`/blog/${j.slug}`} className="card-prem overflow-hidden group cursor-pointer h-full block" data-cursor="hover">
              <div className="aspect-[16/10] overflow-hidden">
                <img src={j.cover} alt={j.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1200ms]"/>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 text-[11px] uppercase tracking-widest text-dim">
                  <span className="text-accent">{j.cat}</span><span>&bull;</span><span>{j.date}</span><span>&bull;</span><span>{j.read}</span>
                </div>
                <h3 className="font-display text-xl mt-4">{j.title}</h3>
                <p className="text-muted text-[13px] mt-3 leading-relaxed">{j.excerpt}</p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default Home;
